// Third-party
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';

// Components
import DataTable from '@/views/components/data/DataTable';
import FileUploadModal from '@/views/components/modals/admin/FileUploadModal';
import ErrorLogModal from '@/views/components/modals/admin/ErrorLogModal';
import { PrimaryButton } from '@/views/components/buttons';
import { PillLabel } from '@/views/components/misc';
import StatusCell from '@/views/components/cells/admin-portal/StatusCell';
import { Tooltip } from '@/views/components/tooltips';

// Types
import Locale from '@/types/locale';
import { DataUploadPortalConfig, DataUploadPortalGroupType, DataUploadPortalStatus, DataUploadPortalFile } from '@/types/data-upload-config';
import { ReduxState } from '@/redux/modules/types';

// Utils
import FormattedMessage from '@/utils/i18n/FormattedMessage';
import { LOCALE, formatMessage } from '@/utils/i18n';

type StateProps = {
  locale: Locale;
};

type Props = StateProps & {
  data: Array<DataUploadPortalConfig>;
  onImportFiles: (type: DataUploadPortalGroupType, configKey: string) => void;
  onCancelImport: (type: string, configKey: string) => void;
};

const GroupDataUpload: React.FC<Props> = (props: Props) => {
  const { locale, data, onImportFiles, onCancelImport } = props;

  const [tableData, setTableData] = useState<Array<DataUploadPortalConfig>>([]);
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
  const [showErrorLogs, setShowErrorLogs] = useState<boolean>(false);

  const [modalConfig, setModalConfig] = useState<DataUploadPortalConfig | undefined>(undefined);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const showModal = (config: DataUploadPortalConfig) => {
    const { dataUpload } = config;
    const status = dataUpload?.status;
    setModalConfig(config);
    status === DataUploadPortalStatus.FAILED ? setShowErrorLogs(true) : setShowFileUpload(true);
  };

  const getColumns = () => [
    {
      key: 'job_name',
      accessor: 'job_name',
      width: 220,
      Header: () => <FormattedMessage id="Admin.DataUpload.DataTable.JobName" locale={locale} />,
      Cell: ({ value }: { value: string }) => (value ? <span>{value}</span> : null),
    },
    {
      key: 'status',
      accessor: 'status',
      width: 200,
      Header: () => <FormattedMessage id="Admin.DataUpload.DataTable.Status" locale={locale} />,
      Cell: ({ original, value }: { original: DataUploadPortalConfig; value: string }) => (
        <StatusCell value={value} original={original} type={DataUploadPortalGroupType.Groups} onCancelImport={onCancelImport} />
      ),
    },
    {
      key: 'files',
      accessor: 'files',
      width: 100,
      Header: () => <FormattedMessage id="Admin.DataUpload.DataTable.Files" locale={locale} />,
      Cell: ({ value }: { value: Array<DataUploadPortalFile> }) => {
        const total = value.length;

        if (!total || total === 0) return <i className="icon-minus-med data-empty-icon" />;

        return getUploadedFiles(value);
      },
    },
    {
      key: 'last_updated',
      accessor: 'last_updated',
      width: 250,
      Header: () => <FormattedMessage id="Admin.DataUpload.DataTable.LastUpdated" locale={locale} />,
      Cell: ({ value }: { value: string }) => (value ? <span>{value}</span> : null),
    },
    {
      key: 'access',
      accessor: 'access',
      Header: () => <FormattedMessage id="Admin.DataUpload.DataTable.Access" locale={locale} />,
      Cell: ({ value }: { value: string }) => (value ? <span className="data-cell-access">{value}</span> : null),
    },
    {
      key: 'upload_button',
      accessor: 'upload_button',
      width: locale === LOCALE.FR_CA ? 108 : 88,
      Cell: ({ value }: { value: any }) => {
        const { files, dataUpload } = value.config;
        const status = dataUpload?.status;
        const uploadedFiles = files.filter((file: DataUploadPortalFile) => file.createdAt && file.url);

        return (
          <PrimaryButton
            onClick={() => showModal(value.config)}
            className={classNames('data-upload-button', { 'is-update': uploadedFiles.length > 0 && status && status !== DataUploadPortalStatus.FAILED })}
            disabled={status === DataUploadPortalStatus.ONGOING}
          >
            {getButtonLabel(value.config)}
          </PrimaryButton>
        );
      },
    },
  ];

  const getTableData = (configs: Array<DataUploadPortalConfig>) => {
    if (!configs.length) return [];

    return configs.map((config: any) => {
      const uploadedFiles = config.files.filter((file: DataUploadPortalFile) => file.createdAt && file.url);
      const last_updated = uploadedFiles.length > 0 ? [...uploadedFiles].sort((a: any, b: any) => moment(a.createdAt).diff(b.createdAt))[0] : null;
      const workflow = config.workflow ? config.workflow : null;
      const dataUpload = config.dataUpload ? config.dataUpload : null;
      const status = config.dataUpload && config.dataUpload.status ? config.dataUpload.status : null;

      return {
        key: config.configKey,
        job_name: config.name,
        last_updated: last_updated ? (
          moment(last_updated && last_updated.createdAt).format(formatMessage('Common.DateFormats.DateWithTime'))
        ) : (
          <i className="icon-minus-med data-empty-icon" />
        ),
        // TODO: Connect with Group Name Structure in story: https://capintel.atlassian.net/browse/CONN-5405
        access: config.groups.targetedGroups.names.join(', '),
        // TODO: Connect with Upload Modal in story: https://capintel.atlassian.net/browse/CONN-5394
        upload_button: { config },
        files: config.files,
        workflow,
        dataUpload,
        status,
      };
    });
  };

  const getButtonLabel = (config: DataUploadPortalConfig) => {
    const { files, dataUpload } = config;
    const status = dataUpload?.status;
    const uploadedFiles = files.filter((file: DataUploadPortalFile) => file.createdAt && file.url);

    if (status === DataUploadPortalStatus.FAILED) {
      return formatMessage('Admin.DataUpload.DataTable.Review');
    }

    if (status && uploadedFiles.length > 0) {
      return formatMessage('Admin.DataUpload.DataTable.Update');
    }

    return formatMessage('Admin.DataUpload.DataTable.Upload');
  };

  const getUploadedFiles = (files: Array<DataUploadPortalFile>) => {
    const uploadedFiles = files.filter((file: DataUploadPortalFile) => file.createdAt && file.url);

    if (!uploadedFiles || uploadedFiles.length === 0) return <i className="icon-minus-med data-empty-icon" />;

    return (
      <div className="pill-wrapper files-pill">
        <Tooltip className="d-table-cell" message={getFilesTooltipMessage(uploadedFiles)}>
          <div className="files-amount">
            <i className="icon-single-file" />
            <PillLabel key={uploadedFiles.length} className="status-pill" text={`${uploadedFiles.length}`} />
          </div>
        </Tooltip>
      </div>
    );
  };

  const getFilesTooltipMessage = (files: any) =>
    files.map((file: DataUploadPortalFile, index: number) => {
      const uploadedFile = file?.url?.split('/').pop() || '';

      return (
        <span key={file?.name || index} className="file-info">
          <span className="file-info-name">{file?.name || ''}</span>
          <span>{uploadedFile}</span>
        </span>
      );
    });

  return tableData.length ? (
    <div className="data-upload data-container">
      <span className="data-table-header">
        <FormattedMessage id="Admin.DataUpload.GroupWide.Title" locale={locale} />
      </span>
      <DataTable className="group-data-jobs data-upload-table" columns={getColumns()} data={getTableData(tableData)} />
      {showFileUpload && modalConfig && (
        <FileUploadModal
          show={showFileUpload}
          onCancel={() => setShowFileUpload(false)}
          onImportFiles={configKey => {
            if (onImportFiles(DataUploadPortalGroupType.Groups, configKey) !== undefined) {
              setModalConfig(undefined);
              setShowFileUpload(false);
            }
          }}
          config={modalConfig}
          locale={locale}
        />
      )}
      {showErrorLogs && modalConfig && (
        <ErrorLogModal config={modalConfig} show={showErrorLogs} onCancel={() => setShowErrorLogs(false)} showFileUpload={() => setShowFileUpload(true)} />
      )}
    </div>
  ) : null;
};

const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
});

export default connect(mapStateToProps, {})(GroupDataUpload);
